import { motion } from 'framer-motion';
import { ChevronRight, Zap } from 'lucide-react';
import { DEPARTURES, KPI, META } from './data';

// HOME — Live departure board hero. NO image hero. Pure data, mono-type.
// Distinctive vs every other template in the store.
export default function Home({ setPage }) {
  const statusColor = {
    BOARDING: 'text-[#5FFB7A]', CLEARED: 'text-[#5FFB7A]', TAXI: 'text-[#FFC93B]',
    POSITIONING: 'text-[#EAF1F4]/70', SCHED: 'text-[#EAF1F4]/55',
  };
  return (
    <div className="bg-[#0F0F11] text-[#EAF1F4]">
      {/* Hero — split: lead copy left, departure board right (the "screen") */}
      <section className="border-b border-[#EAF1F4]/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 pb-12 grid lg:grid-cols-[1fr_1.4fr] gap-10 items-center">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26] mb-5">⏵ Operations Centre / Live</p>
            <h1 className="font-serif text-5xl lg:text-7xl xl:text-[88px] leading-[0.92] tracking-[-0.025em]">
              Aircraft, <span className="italic text-[#D73B26]">on the line</span>.<br />Quoted in fourteen minutes.
            </h1>
            <p className="text-base text-[#EAF1F4]/65 mt-8 leading-[1.7] max-w-md">A Part 135 jet operator, not a broker. The aircraft are ours. The captains are ours. The maintenance is ours. The price is the price.</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button onClick={() => setPage('quote')} className="bg-[#D73B26] text-[#0F0F11] px-7 py-3.5 font-mono text-[10px] tracking-[0.28em] uppercase hover:bg-[#EAF1F4] transition-colors flex items-center gap-2" data-testid="jet-home-quote-cta">
                <Zap className="w-3.5 h-3.5" strokeWidth={2} /> Get a quote
              </button>
              <button onClick={() => setPage('fleet')} className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#EAF1F4]/70 underline-offset-8 hover:text-[#D73B26] hover:underline">
                See the fleet
              </button>
            </div>
          </div>
          {/* Departure board — terminal */}
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }} className="border border-[#EAF1F4]/15 bg-[#06060A] overflow-hidden">
            <div className="bg-[#0F0F11] border-b border-[#EAF1F4]/10 px-5 py-2.5 flex items-center justify-between font-mono text-[9px] tracking-[0.3em] uppercase">
              <span className="text-[#D73B26]">/ Today · Live Departures</span>
              <span className="flex items-center gap-1.5 text-[#5FFB7A]"><span className="w-1.5 h-1.5 rounded-full bg-[#5FFB7A] animate-pulse" /> OPS</span>
            </div>
            <table className="w-full font-mono text-[11px]">
              <thead>
                <tr className="text-[#EAF1F4]/40 text-[9px] tracking-[0.25em] uppercase border-b border-[#EAF1F4]/10">
                  <th className="text-left p-3">Tail</th>
                  <th className="text-left p-3">From</th>
                  <th className="text-left p-3">To</th>
                  <th className="text-left p-3 hidden sm:table-cell">Dep</th>
                  <th className="text-left p-3 hidden md:table-cell">Type</th>
                  <th className="text-right p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {DEPARTURES.map((d, i) => (
                  <motion.tr key={d.tail + i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 + i * 0.1 }} className="border-b border-[#EAF1F4]/5 hover:bg-[#EAF1F4]/[0.03]">
                    <td className="p-3 text-[#D73B26]">{d.tail}</td>
                    <td className="p-3">{d.from}</td>
                    <td className="p-3 text-[#EAF1F4]/85">{d.to}</td>
                    <td className="p-3 hidden sm:table-cell text-[#EAF1F4]/55">{d.dep}</td>
                    <td className="p-3 hidden md:table-cell text-[#EAF1F4]/55">{d.type}</td>
                    <td className={`p-3 text-right ${statusColor[d.status] || 'text-[#EAF1F4]/55'}`}>{d.status}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
            <div className="bg-[#0F0F11] border-t border-[#EAF1F4]/10 px-5 py-2 font-mono text-[9px] tracking-[0.25em] uppercase text-[#EAF1F4]/40 flex justify-between">
              <span>Updated every 30s · Mock for demonstration</span>
              <span>{META.tail}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* KPI strip — large number panels */}
      <section className="border-b border-[#EAF1F4]/10 bg-[#06060A]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {KPI.map((k, i) => (
            <div key={k.l} className="border border-[#EAF1F4]/10 p-5">
              <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#D73B26]">/ Metric 0{i + 1}</p>
              <p className="font-serif text-4xl lg:text-5xl text-[#EAF1F4] mt-3 leading-none">{k.v}</p>
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#EAF1F4]/50 mt-3">{k.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* "How we quote" — three steps as numbered terminal lines */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26] mb-3">⏵ Quotation procedure</p>
        <h2 className="font-serif text-4xl lg:text-6xl mb-12 leading-[0.95]">From enquiry to lift, <span className="italic text-[#D73B26]">in hours</span>.</h2>
        <div className="grid md:grid-cols-3 gap-px bg-[#EAF1F4]/10">
          {[
            { n: '01', t: 'Enquiry received', d: 'Manual review by an ops dispatcher within fourteen minutes, twenty-four hours a day. No bots. No auto-quotes.' },
            { n: '02', t: 'Aircraft selected', d: 'Match against availability, range, runway, and your specific cabin requirements. We will tell you if a competitor has a better fit.' },
            { n: '03', t: 'Firm price issued', d: 'Single line on the contract. No fuel surcharge, no positioning fee, no overflight surprises. Lift confirmed within four hours of signature.' },
          ].map(s => (
            <div key={s.n} className="bg-[#0F0F11] p-8 lg:p-10">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D73B26]">{`/ Step ${s.n}`}</p>
              <h3 className="font-serif text-3xl mt-3 leading-tight">{s.t}</h3>
              <p className="text-sm text-[#EAF1F4]/65 mt-4 leading-[1.7]">{s.d}</p>
            </div>
          ))}
        </div>
        <button onClick={() => setPage('quote')} className="mt-12 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] uppercase text-[#D73B26] hover:text-[#EAF1F4]">
          Open the quote terminal <ChevronRight className="w-3 h-3" strokeWidth={2} />
        </button>
      </section>
    </div>
  );
}
